import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router/composables';
import { CalendarEvent } from '@/services/api-providers/CalendarServiceProvider';
import apiService from '@/services/ApiService';
import notificationService from '@/services/NotificationService';
import urlService from '@/services/UrlService';
import EventBus from '@/EventBus';
import { OPEN_BOT_SETTINGS } from '@/events';
import { DRAFT_EDIT } from '@/routerNames';
import appStore from '@/store';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { Article } from '@/models/article/Article';
import { Workspace } from '@/models/Workspace';
import { formatTime } from '@/helpers/calendarUtils';
import slugify from 'slugify';

type RecurringAction = 'invite' | 'remove' | 'skip' | null;

export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarEventPopover',
  props: {
    event: null,
    position: null
  },
  emits: ["close", "bot-status-changed", "navigate-to-list"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'bot-status-changed', payload: { eventId: string; newStatus: string; scope?: string }): void;
  (e: 'navigate-to-list'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  event: CalendarEvent;
  position: { top: number; left: number };
}

const PLATFORM_LABELS: Record<string, string> = {
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
  zoom: 'Zoom',
  other: 'Video Conference',
};





const router = useRouter();

const popoverRef = ref<HTMLElement | null>(null);
const isActionLoading = ref(false);

const recurringAction = ref<RecurringAction>(null);

const botStatus = computed(() => props.event.botInviteStatus || 'none');

/** Show gear icon when bot is invited/accepted and event has conference url */
const showGearIcon = computed(() => {
  const status = botStatus.value;
  return props.event.conferenceUrl && (status === 'invited' || status === 'accepted' || status === 'inviting');
});

const positionStyle = computed(() => ({
  top: `${props.position.top}px`,
  left: `${props.position.left}px`,
}));

const barColor = computed(() => {
  const statusColors: Record<string, string> = {
    none: '#B8BAC0',
    accepted: '#2E5BFF',
    recorded: '#1B8A2A',
    invited: '#2E5BFF',
    inviting: '#2E5BFF',
    failed: '#B3261E',
    skipped: '#6B7280',
    declined: '#B3261E',
  };
  return statusColors[botStatus.value] || '#B8BAC0';
});

const timeDisplay = computed(() => {
  const start = new Date(props.event.startTime);
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayName = dayNames[start.getDay()];
  const month = monthNames[start.getMonth()];
  const day = start.getDate();
  const startTime = formatTime(props.event.startTime);
  const endTime = formatTime(props.event.endTime);
  return `${dayName}, ${month} ${day} \u00B7 ${startTime} \u2013 ${endTime}`;
});

const safeConferenceUrl = computed((): string | null => {
  const url = props.event.conferenceUrl;
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:'].includes(parsed.protocol) ? url : null;
  } catch {
    return null;
  }
});

const platformLabel = computed(() => {
  const platform = props.event.conferencePlatform || 'other';
  return PLATFORM_LABELS[platform] || 'Video Conference';
});

// ---- Recurring scope handlers (P2) ----

const handleInviteOrScope = () => {
  if (props.event.isRecurring) {
    recurringAction.value = 'invite';
  } else {
    handleInvite();
  }
};

const handleRemoveOrScope = () => {
  if (props.event.isRecurring) {
    recurringAction.value = 'remove';
  } else {
    handleRemove();
  }
};

const handleScopedInvite = async (scope: 'single' | 'all') => {
  isActionLoading.value = true;
  try {
    const { data } = await apiService.calendar.inviteBot(props.event._id, { scope });
    emit('bot-status-changed', { eventId: props.event._id, newStatus: data.botInviteStatus, scope });
  } catch (e: unknown) {
    notificationService.error(getApiErrorMessage(e, 'Failed to invite bot'));
  } finally {
    isActionLoading.value = false;
    recurringAction.value = null;
  }
};

const handleScopedRemove = async (scope: 'single' | 'all') => {
  isActionLoading.value = true;
  try {
    const { data } = await apiService.calendar.removeBot(props.event._id, { scope });
    emit('bot-status-changed', { eventId: props.event._id, newStatus: data.botInviteStatus, scope });
  } catch (e: unknown) {
    notificationService.error(getApiErrorMessage(e, 'Failed to remove bot'));
  } finally {
    isActionLoading.value = false;
    recurringAction.value = null;
  }
};

// ---- Bot actions (non-recurring, call API directly) ----

const getApiErrorMessage = (e: unknown, fallback: string): string => {
  const err = e as { response?: { data?: { error?: string } } };
  return err?.response?.data?.error || fallback;
};

const handleInvite = async () => {
  isActionLoading.value = true;
  try {
    const { data } = await apiService.calendar.inviteBot(props.event._id);
    emit('bot-status-changed', { eventId: props.event._id, newStatus: data.botInviteStatus });
  } catch (e: unknown) {
    notificationService.error(getApiErrorMessage(e, 'Failed to invite bot'));
  } finally {
    isActionLoading.value = false;
  }
};

const handleRemove = async () => {
  isActionLoading.value = true;
  try {
    const { data } = await apiService.calendar.removeBot(props.event._id);
    emit('bot-status-changed', { eventId: props.event._id, newStatus: data.botInviteStatus });
  } catch (e: unknown) {
    notificationService.error(getApiErrorMessage(e, 'Failed to remove bot'));
  } finally {
    isActionLoading.value = false;
  }
};

const handleUnskip = async () => {
  isActionLoading.value = true;
  try {
    const { data } = await apiService.calendar.unskipEvent(props.event._id);
    emit('bot-status-changed', { eventId: props.event._id, newStatus: data.botInviteStatus });
  } catch (e: unknown) {
    notificationService.error(getApiErrorMessage(e, 'Failed to unskip event'));
  } finally {
    isActionLoading.value = false;
  }
};

// ---- P5: Gear icon opens global bot settings ----

const handleOpenSettings = () => {
  // For recurring events use recurringEventId so all occurrences share one config.
  // For non-recurring use providerEventId. Must match getAllMeetingsAction logic.
  const configId =
    props.event.isRecurring && props.event.recurringEventId
      ? props.event.recurringEventId
      : props.event.providerEventId;
  EventBus.$emit(OPEN_BOT_SETTINGS, {
    id: configId,
    title: props.event.title,
    organizedBy: props.event.organizerEmail,
    startAt: props.event.startTime,
    status: 'upcoming',
  });
};

// ---- Recorded: navigate to article/draft ----

const navigateToPublishedArticle = async (linked: NonNullable<CalendarEvent['linkedArticle']>) => {
  if (!linked.slug || !linked.shortId || !linked.workspaceShortId) {
    notificationService.warning('Not enough data to open article');
    return;
  }
  const article: Partial<Article> = {
    id: linked.articleId,
    slug: slugify(linked.slug),
    shortId: linked.shortId,
  };
  const workspace: Partial<Workspace> = {
    shortId: linked.workspaceShortId,
    title: linked.workspaceTitle?.toLowerCase(),
  };
  await router.push(urlService.getArticleViewRoute(article, workspace, ArticleEditMode.VIDEO));
};

const handleViewRecording = async () => {
  const linked = props.event.linkedArticle;

  if (!linked || !linked.articleId) {
    // Fallback: no article data available, switch to list view
    emit('navigate-to-list');
    emit('close');
    return;
  }

  try {
    // Switch company if the recording is saved in a different company
    const currentCompanyId = appStore.state.company?.id;
    if (linked.companyId && linked.companyId !== currentCompanyId) {
      // Find the target company in the user's companies to get its domain
      const userCompanies = appStore.state.userCompanies as Array<{
        id: string;
        getDomainAddress?: () => string;
      }>;
      const targetCompany = userCompanies?.find((c) => c.id === linked.companyId);

      if (!targetCompany) {
        notificationService.warning('You do not have access to the company containing this recording');
        emit('close');
        return;
      }

      // Switch company session on the backend — this sets the shared parent-domain cookie
      await apiService.company.switch(linked.companyId);

      // Build the full URL including the target company's domain.
      // Cookies are set on `.snaplore.com` parent domain so auth carries over automatically.
      const articlePath = linked.isDraft
        ? `/draft/${linked.articleId}`
        : `/b/${linked.workspaceShortId}/${slugify(linked.workspaceTitle?.toLowerCase() || '')}/${
            linked.shortId
          }/${slugify(linked.slug || '')}/video`;

      const domainAddress = targetCompany.getDomainAddress?.() || '';
      window.location.href = `${domainAddress}/#${articlePath}`;
      return;
    }

    if (linked.isDraft) {
      await router.push({ name: DRAFT_EDIT, params: { id: linked.articleId } });
    } else {
      await navigateToPublishedArticle(linked);
    }
    emit('close');
  } catch {
    // Navigation failed (e.g., same route), just close
    emit('close');
  }
};

// ---- Click outside to close ----

const onMouseDown = (e: MouseEvent) => {
  if (popoverRef.value && !popoverRef.value.contains(e.target as Node)) {
    emit('close');
  }
};

onMounted(() => {
  document.addEventListener('mousedown', onMouseDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onMouseDown);
});

return { __sfc: true,PLATFORM_LABELS, props, emit, router, popoverRef, isActionLoading, recurringAction, botStatus, showGearIcon, positionStyle, barColor, timeDisplay, safeConferenceUrl, platformLabel, handleInviteOrScope, handleRemoveOrScope, handleScopedInvite, handleScopedRemove, getApiErrorMessage, handleInvite, handleRemove, handleUnskip, handleOpenSettings, navigateToPublishedArticle, handleViewRecording, onMouseDown }
}

})